import { Typography } from 'antd';
import clsx from 'clsx';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { isSafeHref, normalizeHref } from '../../lib/markdown';
import styles from './BodyRender.module.scss';

const DISALLOWED = [
  'h1',
  'h4',
  'h5',
  'h6',
  'code',
  'pre',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'hr',
  'img',
  'input',
];

type Props = {
  source: string;
  className?: string;
};

/**
 * Renders trusted-but-user-authored markdown (idea bodies, praise stories) to a
 * constrained, sanitised subset of HTML. Shared across features — keep it generic.
 */
export const BodyRender = ({ source, className }: Props) => {
  return (
    <div className={clsx(styles.render, className)}>
      <ReactMarkdown
        skipHtml
        remarkPlugins={[remarkGfm, remarkBreaks]}
        disallowedElements={DISALLOWED}
        unwrapDisallowed
        components={{
          a: ({ href, children }) => {
            const normalized = normalizeHref(href);
            return isSafeHref(normalized) ? (
              <a href={normalized} target="_blank" rel="noopener noreferrer nofollow ugc">
                {children}
              </a>
            ) : (
              <span>{children}</span>
            );
          },
          h2: ({ children }) => (
            <Typography.Title level={3} style={{ margin: '16px 0 8px' }}>
              {children}
            </Typography.Title>
          ),
          h3: ({ children }) => (
            <Typography.Title level={4} style={{ margin: '12px 0 6px' }}>
              {children}
            </Typography.Title>
          ),
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
};
